import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
// Ensure these paths are correct relative to Authorization.js
import Login from './login';
import ForgotPasswordPhoneNumber from './ForgotPasswordPhoneNumber';
import ForgotPasswordOTP from './ForgotPasswordNewPassword'; // This file contains the OTP verification logic
import ForgotPasswordNewPassword from './ForgotPasswordOTP'; // This file contains the New Password setting logic

// Import Amplify Auth functions needed for session check (signOut is still needed for explicit logout)
import { signOut } from 'aws-amplify/auth';

// Import typography styles
import { h1, p } from '../../styles/typography';


// Utility to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism background styles (defined but not directly used in this component's main div style)
const glassBg = 'bg-white/10 backdrop-blur-2xl rounded-xl border border-white/10 shadow-lg';

// --- Note: AWS Amplify Configuration is now handled in src/index.js ---


const Authorization = ({ onAuthSuccess, onLogoutSuccess }) => { // Added onLogoutSuccess prop
    // State to manage which authentication view is currently active
    const [currentView, setCurrentView] = useState('login'); // 'login', 'forgot-phone', 'forgot-otp', 'forgot-new-password'
    const [phoneNumberForOTP, setPhoneNumberForOTP] = useState(''); // State to potentially pass phone number (though current flow uses email/username)
    const [emailForForgotPassword, setEmailForForgotPassword] = useState(''); // Stores identifier (email/username) for forgot password flow
    // Set isLoadingAuth to false by default, as we are no longer checking for an existing session on mount.
    const [isLoadingAuth, setIsLoadingAuth] = useState(false);

    // Removed the useEffect hook that checks for an existing authenticated session on component mount.
    // This ensures the user is always presented with the login screen.
    /*
    useEffect(() => {
        const checkAuthSession = async () => {
            try {
                const { tokens } = await fetchAuthSession();
                // If tokens exist (accessToken and idToken), a user is signed in
                if (tokens && tokens.accessToken && tokens.idToken) {
                    console.log('User already signed in, redirecting to home.');
                    onAuthSuccess(); // Redirect to dashboard
                } else {
                    console.log('No active session found.');
                }
            } catch (error) {
                console.error('Error checking auth session:', error);
                // No active session or error fetching, proceed to login screen
            } finally {
                setIsLoadingAuth(false); // Authentication check is complete
            }
        };

        checkAuthSession();
    }, [onAuthSuccess]);
    */


    // Log state changes for debugging purposes
    useEffect(() => {
        console.log("Current Auth View:", currentView);
    }, [currentView]);

    // Fonts are now loaded in index.html for better performance

    // Function to handle logout and navigate back to login
    const handleLogout = async () => {
        try {
            await signOut();
            console.log('User signed out successfully.');
            onLogoutSuccess(); // Callback to reset app state and show login
            setCurrentView('login'); // Ensure current view is login
            setEmailForForgotPassword(''); // Clear any stored identifier
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };


    // Renders the appropriate authentication component based on the currentView state
    const renderAuthView = () => {
        // Since we removed the session check, isLoadingAuth will always be false,
        // so we can remove this conditional loading block.
        /*
        if (isLoadingAuth) {
            return (
                <div className="flex items-center justify-center h-full">
                    <p className="text-gray-900 text-lg font-open-sans">Checking session...</p>
                </div>
            );
        }
        */

        switch (currentView) {
            case 'login':
                return (
                    <Login
                        onLoginSuccess={onAuthSuccess}
                        onNavigateToForgotPassword={(identifier) => { // Accepts email/username as identifier
                            console.log("Navigating to forgot-phone from Login. User:", identifier);
                            setEmailForForgotPassword(identifier); // Store the identifier
                            setCurrentView('forgot-phone');
                        }}
                    />
                );
            case 'forgot-phone': // This component initiates sending the OTP (to email/username)
                return (
                    <ForgotPasswordPhoneNumber
                        onSendOTP={(identifier) => { // Receives identifier (email/username) after sending code
                            console.log("Identifier submitted. Navigating to forgot-otp. Identifier:", identifier);
                            setEmailForForgotPassword(identifier); // Ensure the identifier is stored for OTP step
                            setCurrentView('forgot-otp');
                        }}
                        onBack={() => {
                            console.log("Navigating back to login from forgot-phone.");
                            setCurrentView('login');
                        }}
                        initialIdentifier={emailForForgotPassword} // Pass stored identifier for pre-population
                    />
                );
            case 'forgot-otp': // This is the OTP verification step
                return (
                    <ForgotPasswordOTP
                        identifier={emailForForgotPassword} // Pass the identifier to verify OTP
                        onVerifyOTP={() => {
                            console.log("OTP Verified. Navigating to forgot-new-password.");
                            setCurrentView('forgot-new-password');
                        }}
                        onBack={() => {
                            console.log("Navigating back to forgot-phone from forgot-otp.");
                            setCurrentView('forgot-phone');
                        }}
                    />
                );
            case 'forgot-new-password': // This is the Set New Password step
                return (
                    <ForgotPasswordNewPassword
                        identifier={emailForForgotPassword} // Pass the identifier to set new password
                        onPasswordChangeSuccess={() => {
                            console.log("Password changed. Navigating back to login.");
                            setCurrentView('login');
                        }}
                        onBack={() => {
                            console.log("Navigating back to forgot-otp from forgot-new-password.");
                            setCurrentView('forgot-otp');
                        }}
                    />
                );
            default:
                // Default to login view if an unknown state is encountered
                return (
                    <Login
                        onLoginSuccess={onAuthSuccess}
                        onNavigateToForgotPassword={(identifier) => {
                            setEmailForForgotPassword(identifier);
                            setCurrentView('forgot-phone');
                        }}
                    />
                );
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col md:flex-row items-center justify-center p-4 font-figtree relative overflow-hidden"
            style={{ background: 'radial-gradient(50% 50% at 50% 50%, #BBF246 0%, #6C8C28 100%)' }}
        >
            {/* Abstract 3D shapes in the background for visual flair */}
            <motion.div
                className="absolute inset-0 z-0 opacity-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.2 }}
                transition={{ duration: 1.5 }}
            >
                {/* Various colored and shaped divs to create a subtle background effect */}
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white rounded-full mix-blend-overlay" />
                <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-green-300 rounded-full mix-blend-overlay" />
                <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-500 rounded-full mix-blend-overlay" />
                <div className="absolute top-1/4 right-1/2 w-40 h-40 bg-green-700 rounded-full mix-blend-overlay" />

                {/* Larger geometric shapes with borders */}
                <div className="absolute top-10 left-10 w-40 h-40 border-4 border-green-400 rounded-lg transform rotate-45 opacity-50" />
                <div className="absolute bottom-20 right-20 w-32 h-32 border-4 border-white rounded-full transform rotate-12 opacity-50" />
                <div className="absolute top-1/3 right-10 w-24 h-24 border-4 border-green-200 rounded-md transform -rotate-30 opacity-50" />
            </motion.div>

            {/* Left side content: Welcome message and app description */}
            <motion.div
                className="relative z-10 text-center md:text-left text-white md:w-1/2 p-8"
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
            >
                <h1 className={`${h1} mb-4`}>
                    Welcome to <span className="text-[#A7F3D0]">Innoworx.ai</span>
                </h1>
                <p className={`${p} text-gray-200`}>
                    Smart, seamless attendance tracking for your workforce
                </p>
            </motion.div>

            {/* Authentication forms container with glassmorphism style */}
            <motion.div
                className={combineClasses(
                    'relative z-20',
                    'flex items-center flex-shrink-0',
                    'rounded-xl backdrop-blur-3xl',
                    'gap-[10px]'
                )}
                style={{
                    width: '559px',
                    height: '642px',
                    padding: '100px 69px',
                    background: 'linear-gradient(151deg, rgba(255, 255, 255, 0.40) 0%, rgba(255, 255, 255, 0.00) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.00)'
                }}
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
            >
                {renderAuthView()}
            </motion.div>
        </div>
    );
};

export default Authorization;
